import { ArrowRight } from "lucide-react";
import { Badge, Button, Icon, Inline } from "@generalapplications/ui";

import { StatusBadge } from "@/app/common/components/composite/feedback/StatusBadge";
import {
  RevealGroup,
  RevealItem,
} from "@/app/common/components/composite/motion/RevealGroup";
import { isPreviewableService, type ServiceRecord } from "@/content/services";
import { routes } from "@/app/common/functions/routes/routes";
import { formatText } from "@/app/common/functions/format/formatText";

export type ServiceChipsProps = {
  services: readonly ServiceRecord[];
  ariaLabel: string;
};

// The third affordance tier on the site, and deliberately the loudest:
//
//   1. offering/applicableWork prose (OfferingList) — scope statements,
//      never controls.
//   2. related capability/industry links (RelatedLinkList) — lateral
//      navigation, quiet outline buttons.
//   3. service chips (here) — the drill-down into commercial detail, so they
//      carry an arrow.
//
// A private-but-previewable service is named, not navigable: a badge with no
// arrow, so nothing about it says "control".
//
// Tier is deliberately NOT shown here. A bordered tag inside a bordered chip
// reads as a badge inside a badge; the tier claim is made once, in prose, on
// the service's own page (ServiceTemplate.tsx). See
// docs/product/policies/content/text.md §7.
export function ServiceChips({ services, ariaLabel }: ServiceChipsProps) {
  if (services.length === 0) return null;

  return (
    <RevealGroup>
      <Inline as="ul" aria-label={ariaLabel} wrap gap="sm">
        {services.map((service) => {
          const label = formatText(service.shortTitle ?? service.title);
          const status =
            service.status !== "published" ? (
              <StatusBadge status={service.status} />
            ) : null;

          return (
            <RevealItem as="li" key={service.slug}>
              {isPreviewableService(service) ? (
                <Button
                  href={routes.services.detail(service.slug)}
                  variant="outline"
                  size="xl"
                  weight="bold"
                >
                  {label}
                  {status}
                  <Icon icon={ArrowRight} placement="end" />
                </Button>
              ) : (
                <Badge variant="outline" tone="muted" size="lg">
                  {label}
                  {status}
                </Badge>
              )}
            </RevealItem>
          );
        })}
      </Inline>
    </RevealGroup>
  );
}
